/**
 * O valor em reais escrito por extenso.
 *
 * O recibo é o documento em que o número sozinho não basta: `R$ 1.500,00` pode
 * virar `R$ 15.000,00` com uma vírgula fora do lugar, e quem assina responde pelo
 * que está escrito. Por isso o recibo brasileiro repete o valor em letras —
 * `R$ 1.500,00 (mil e quinhentos reais)` — e é isso que este módulo escreve.
 *
 * ## Como ler o que a pessoa digita
 *
 * Aceita `R$ 1.500,00`, `1.500,00`, `1500,00`, `1500`, `1.500` e `1500.00`. A
 * regra é a do Brasil: **vírgula é decimal; ponto com três dígitos é milhar**.
 * Quando não dá para entender (`abc`, `R$`), devolve `null` e o recibo sai só com
 * o que a pessoa escreveu — melhor um recibo sem o extenso do que um recibo com o
 * valor errado escrito em letras.
 */
/* ------------------------------------------------------------------ */
/* As palavras                                                        */
/* ------------------------------------------------------------------ */
const UNIDADES = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const DEZ_A_DEZENOVE = [
    'dez',
    'onze',
    'doze',
    'treze',
    'catorze',
    'quinze',
    'dezesseis',
    'dezessete',
    'dezoito',
    'dezenove',
];
const DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CENTENAS = [
    '',
    'cento',
    'duzentos',
    'trezentos',
    'quatrocentos',
    'quinhentos',
    'seiscentos',
    'setecentos',
    'oitocentos',
    'novecentos',
];
/** De 1 a 999, com o "e" que a língua pede (`cento e vinte e um`). */
function ate999(numero) {
    if (numero <= 0)
        return '';
    if (numero === 100)
        return 'cem';
    const centena = Math.floor(numero / 100);
    const resto = numero % 100;
    const partes = [];
    if (centena > 0)
        partes.push(CENTENAS[centena]);
    if (resto > 0) {
        if (resto < 10)
            partes.push(UNIDADES[resto]);
        else if (resto < 20)
            partes.push(DEZ_A_DEZENOVE[resto - 10]);
        else {
            const dezena = Math.floor(resto / 10);
            const unidade = resto % 10;
            partes.push(unidade > 0 ? `${DEZENAS[dezena]} e ${UNIDADES[unidade]}` : DEZENAS[dezena]);
        }
    }
    return partes.join(' e ');
}
/**
 * O número inteiro por extenso, de zero a 999 bilhões.
 *
 * O "e" entre os grupos segue o uso brasileiro: entra quando o que vem depois é
 * menor que cem ou é centena redonda — `mil e quinhentos`, `mil e um`,
 * `dois mil e cem`; e não entra em `mil duzentos e trinta e quatro`.
 */
export function inteiroPorExtenso(numero) {
    if (!Number.isFinite(numero))
        return '';
    const inteiro = Math.trunc(Math.abs(numero));
    if (inteiro === 0)
        return 'zero';
    if (inteiro > 999_999_999_999)
        return '';
    const grupos = [
        { divisor: 1_000_000_000, singular: 'bilhão', plural: 'bilhões' },
        { divisor: 1_000_000, singular: 'milhão', plural: 'milhões' },
        { divisor: 1_000, singular: 'mil', plural: 'mil' },
        { divisor: 1, singular: '', plural: '' },
    ];
    const pedacos = [];
    let restante = inteiro;
    for (const grupo of grupos) {
        const quantidade = Math.floor(restante / grupo.divisor);
        restante %= grupo.divisor;
        if (quantidade === 0)
            continue;
        if (grupo.divisor === 1_000) {
            /* "mil", não "um mil". */
            pedacos.push({ texto: quantidade === 1 ? 'mil' : `${ate999(quantidade)} mil`, valor: quantidade * 1_000 });
        }
        else if (grupo.divisor === 1) {
            pedacos.push({ texto: ate999(quantidade), valor: quantidade });
        }
        else {
            const nome = quantidade === 1 ? grupo.singular : grupo.plural;
            pedacos.push({ texto: `${ate999(quantidade)} ${nome}`, valor: quantidade * grupo.divisor });
        }
    }
    let saida = pedacos[0].texto;
    for (const pedaco of pedacos.slice(1)) {
        const usaE = pedaco.valor < 100 || pedaco.valor % 100 === 0;
        saida += `${usaE ? ' e ' : ' '}${pedaco.texto}`;
    }
    return numero < 0 ? `menos ${saida}` : saida;
}
/* ------------------------------------------------------------------ */
/* Reais e centavos                                                   */
/* ------------------------------------------------------------------ */
/** `123456` centavos → `mil duzentos e trinta e quatro reais e cinquenta e seis centavos`. */
export function reaisPorExtenso(centavos) {
    const total = Math.trunc(Math.abs(centavos));
    const parteDosReais = Math.floor(total / 100);
    const parteDosCentavos = total % 100;
    const pedacos = [];
    if (parteDosReais > 0) {
        /*
         * "um milhão **de** reais", mas "um milhão e quinhentos mil reais": o "de" só
         * entra quando o número termina redondo em milhão ou bilhão.
         */
        const de = parteDosReais >= 1_000_000 && parteDosReais % 1_000_000 === 0 ? ' de' : '';
        pedacos.push(`${inteiroPorExtenso(parteDosReais)}${de} ${parteDosReais === 1 ? 'real' : 'reais'}`);
    }
    if (parteDosCentavos > 0) {
        pedacos.push(`${inteiroPorExtenso(parteDosCentavos)} ${parteDosCentavos === 1 ? 'centavo' : 'centavos'}`);
    }
    if (pedacos.length === 0)
        return 'zero reais';
    return `${centavos < 0 ? 'menos ' : ''}${pedacos.join(' e ')}`;
}
/**
 * O que a pessoa digitou no campo do valor, em centavos — ou `null` se não der
 * para entender.
 *
 * `R$ 1.500,00` → `150000`; `1.500` → `150000` (milhar); `1500.00` → `150000`;
 * `R$ 12,50` → `1250`.
 */
export function centavosDoTexto(texto) {
    const limpo = texto
        .replace(/R\$/gi, '')
        .replace(/[\s\u00a0]/g, '')
        .trim();
    if (!limpo)
        return null;
    const negativo = limpo.startsWith('-');
    const corpo = negativo ? limpo.slice(1) : limpo;
    if (!/^[\d.,]+$/.test(corpo))
        return null;
    let reais;
    let centavos = 0;
    if (corpo.includes(',')) {
        /* `1.500,00` — ponto é milhar, vírgula é decimal. */
        const [inteiro = '', decimal = ''] = corpo.split(',');
        if (!/^\d{1,3}(\.\d{3})*$|^\d+$/.test(inteiro))
            return null;
        if (!/^\d{0,2}$/.test(decimal))
            return null;
        reais = Number(inteiro.replace(/\./g, '') || '0');
        centavos = Number(`${decimal}00`.slice(0, 2));
    }
    else if (/^\d{1,3}(\.\d{3})+$/.test(corpo)) {
        /* `1.500` e `1.500.000`: ponto de milhar. */
        reais = Number(corpo.replace(/\./g, ''));
    }
    else if (/^\d+\.\d{1,2}$/.test(corpo)) {
        /* `1500.00` e `12.5`: ponto decimal, do jeito que vem colado de outro sistema. */
        const [inteiro = '', decimal = ''] = corpo.split('.');
        reais = Number(inteiro);
        centavos = Number(`${decimal}00`.slice(0, 2));
    }
    else if (/^\d+$/.test(corpo)) {
        reais = Number(corpo);
    }
    else {
        return null;
    }
    if (!Number.isSafeInteger(reais) || reais > 999_999_999_999)
        return null;
    const total = reais * 100 + centavos;
    return negativo ? -total : total;
}
/** O extenso a partir do que foi digitado: `R$ 1.500,00` → `mil e quinhentos reais`. */
export function valorPorExtenso(texto) {
    const centavos = centavosDoTexto(texto);
    return centavos === null ? null : reaisPorExtenso(centavos);
}
//# sourceMappingURL=valor-por-extenso.js.map